import { Divider, Stack, Tooltip } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { useFormContext } from 'FrontRoyalReactHookForm';
import { Checkbox, TextField } from 'FrontRoyalMaterialUiForm';
import { ContentItemImagePreview, ContentItemImageUpload } from 'ContentItemEditor';
import AssociatedPlaylists from './AssociatedPlaylists';
import EquivalentCoursesList from './EquivalentCoursesList';
import { type EditorCourse, type S3Asset, type StreamFormValues } from './StreamEditor.types';

const REQUIREMENT_IDENTIFIER_TOOLTIP =
    'Courses that share a requirement identifier will have their scores and completion data backfilled when a user defers into a new cohort. If you want "credit" to automatically transfer between courses, ensure they have the same requirement identifier.';

const EXAM_LOCKED_TOOLTIP = 'This setting is disabled now that assessment editing has been locked';

// Wide enough for the 'Exam Time Limit (hours)' label, which is the widest thing in the rail.
const SETTINGS_RAIL_WIDTH = 240;

// A requirement identifier is a short slug, so the field does not need the width of the card.
const IDENTIFIER_FIELD_WIDTH = 420;

type Props = {
    streamId: string;
    image: S3Asset | null | undefined;
    localePackId: string | null | undefined;
    assessmentEditingLocked: boolean;
    uploadingImage: boolean;
    onUploadImage: (files: File[]) => void;
    onRemoveImage: () => void;
    courses: EditorCourse[];
    coursesLoading: boolean;
};

// The flags a learner sees on the course tile. The exam flag is not here: it enables the exam time
// limit, so it sits beside that field.
function CourseFlags() {
    return (
        <Stack>
            <Checkbox name="coming_soon" label="Coming Soon" />
            <Checkbox name="beta" label="Beta" />
            <Checkbox name="just_updated" label="Updated" />
            <Checkbox name="just_added" label="New Course" />
            <Checkbox name="unlocked_for_mba_users" label="Unlocked" />
        </Stack>
    );
}

// oxlint-disable-next-line max-lines-per-function -- the identity fields of the course, in one column
export default function StreamHeaderFields({
    streamId,
    image,
    localePackId,
    assessmentEditingLocked,
    uploadingImage,
    onUploadImage,
    onRemoveImage,
    courses,
    coursesLoading,
}: Props) {
    const { watch } = useFormContext<StreamFormValues>();
    const isExam = !!watch('exam');
    const comingSoon = !!watch('coming_soon');
    const requirementIdentifier = watch('requirement_identifier');

    return (
        // Two regions: what the course is on the left, the switches that change how it behaves
        // in the right rail.
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ alignItems: 'stretch' }}>
            <Stack spacing={3} sx={{ flex: 1, minWidth: 0 }}>
                {/* The image beside the fields it belongs to, rather than above them: a card this
                    wide would otherwise stretch every input across the page. */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ alignItems: 'flex-start' }}>
                    {/*
                        Centred, so the image sits over the middle of the buttons below it. Centring
                        also stops the column from stretching the image and distorting it.

                        The column stretches to the height of the row and pushes its two children
                        apart, which lands the buttons on the same baseline as the bottom of the
                        description beside them. A fixed gap would drift the moment the fields
                        beside it change height.
                    */}
                    <Stack
                        spacing={1}
                        sx={{ alignItems: 'center', alignSelf: 'stretch', justifyContent: 'space-between' }}
                    >
                        <ContentItemImagePreview image={image} alt="The course image" faded={comingSoon} />

                        <ContentItemImageUpload
                            uploading={uploadingImage}
                            onUpload={onUploadImage}
                            onRemove={image ? onRemoveImage : undefined}
                        />
                    </Stack>

                    <Stack spacing={3} sx={{ flex: 1, width: '100%' }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                                name="tag"
                                label="Tag"
                                fullWidth
                                slotProps={{ htmlInput: { dir: 'auto' } }}
                                sx={{ flex: 1 }}
                            />
                            <TextField
                                name="title"
                                label="Title"
                                fullWidth
                                slotProps={{ htmlInput: { dir: 'auto' } }}
                                sx={{ flex: 3 }}
                            />
                        </Stack>

                        <TextField
                            name="description"
                            label="Description"
                            fullWidth
                            multiline
                            rows={4}
                            slotProps={{ htmlInput: { dir: 'auto' } }}
                        />
                    </Stack>
                </Stack>

                <Divider />

                {/* Side by side, split by a rule. Both answer where this course sits among the
                    rest of the content, and stacked they left the bottom of the card empty. */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ alignItems: 'flex-start' }}>
                    <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                        <TextField
                            name="requirement_identifier"
                            label="Requirement Identifier"
                            fullWidth
                            // Capped on the field alone, not on the group. An identifier the length
                            // of 'photography_basics' does not need an input this wide, but a
                            // course pill below it does.
                            sx={{ maxWidth: IDENTIFIER_FIELD_WIDTH }}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <Tooltip title={REQUIREMENT_IDENTIFIER_TOOLTIP}>
                                            <InfoOutlined fontSize="small" />
                                        </Tooltip>
                                    ),
                                },
                            }}
                        />

                        <EquivalentCoursesList
                            requirementIdentifier={requirementIdentifier}
                            streamId={streamId}
                            localePackId={localePackId}
                            courses={courses}
                            loading={coursesLoading}
                        />
                    </Stack>

                    <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />

                    <Stack sx={{ flex: 1, minWidth: 0 }}>
                        <AssociatedPlaylists streamId={streamId} />
                    </Stack>
                </Stack>
            </Stack>

            {/* Only once the regions are side by side. Stacked, the spacing already separates them. */}
            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

            {/* flexShrink, or the left region squeezes the rail below the width of its one field. */}
            <Stack spacing={3} sx={{ width: { xs: '100%', md: SETTINGS_RAIL_WIDTH }, flexShrink: 0 }}>
                <CourseFlags />

                <Divider />

                <Stack spacing={2}>
                    <Tooltip title={assessmentEditingLocked ? EXAM_LOCKED_TOOLTIP : ''}>
                        <span style={{ alignSelf: 'flex-start' }}>
                            <Checkbox name="exam" label="Exam" disabled={assessmentEditingLocked} />
                        </span>
                    </Tooltip>

                    <TextField
                        name="time_limit_hours"
                        label="Exam Time Limit (hours)"
                        type="number"
                        // Parity: the Angular template disabled this too. The Iguana model also
                        // dropped a write to it unless the course was an exam, which is why
                        // fromForm nulls the field when the exam flag goes off.
                        disabled={!isExam}
                    />
                </Stack>
            </Stack>
        </Stack>
    );
}
